import React, { useMemo } from 'react';
import { format } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Plus, Trash2, Edit2, MapPin, Users, AlertTriangle, CalendarDays } from 'lucide-react';
import { formatTimeRange } from '../../utils/time';
import { SERVICE_CONFIG, SERVICE_STYLES } from './constants';

export function ParticipantPicker({ participants, selected, onToggle, maxCount, serviceLine }) {
  const [search, setSearch] = React.useState('');
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    const matched = participants.filter(p => !q || p.name.toLowerCase().includes(q));
    if (serviceLine) {
      matched.sort((a, b) => {
        const aMatch = (a.service_lines || []).includes(serviceLine) ? 0 : 1;
        const bMatch = (b.service_lines || []).includes(serviceLine) ? 0 : 1;
        return aMatch - bMatch;
      });
    }
    return matched;
  }, [participants, search, serviceLine]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-[#2D1B4E] font-medium">Participants ({selected.length}{maxCount ? `/${maxCount}` : ''})</Label>
        {selected.length > 0 && <button onClick={() => onToggle('clear')} className="text-xs text-[#8E85A0] hover:text-[#D9534F]">Clear</button>}
      </div>
      <Input placeholder="Search participants..." value={search} onChange={e => setSearch(e.target.value)} className="border-[#DED8EB] h-8 text-sm" data-testid="participant-picker-search" />
      <ScrollArea className="h-32 border border-[#DED8EB] rounded-md">
        <div className="p-2 space-y-0.5">
          {filtered.map(p => (
            <label key={p.id || p.name} className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-sm ${selected.includes(p.name) ? 'bg-[#EDE7F6]' : 'hover:bg-[#F7F4EF]'}`}>
              <Checkbox checked={selected.includes(p.name)} onCheckedChange={() => onToggle(p.name)} data-testid={`pick-participant-${p.name}`} />
              <span className="text-[#2D1B4E] truncate">{p.name}</span>
            </label>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

export default function ParticipantSlotView({ shifts, date, serviceLine, onEdit, onAssign, onUnassign, onAddSlot, onAddPattern, onBulkAdd }) {
  const config = SERVICE_CONFIG[serviceLine];
  const assigned = useMemo(() => shifts.filter(s => s.employee_id && s.status !== 'unassigned').sort((a, b) => a.time_in.localeCompare(b.time_in)), [shifts]);
  const unassigned = useMemo(() => shifts.filter(s => !s.employee_id || s.status === 'unassigned').sort((a, b) => a.time_in.localeCompare(b.time_in)), [shifts]);
  // Distinct staff working today — same staff covering multiple participants
  // counts ONCE. This is the real headcount.
  const distinctStaff = useMemo(() => new Set(assigned.map(s => s.employee_id)).size, [assigned]);

  return (
    <div data-testid={`${serviceLine}-${date}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-semibold text-[#8E85A0] uppercase tracking-wider">
          {format(new Date(date + 'T12:00:00'), 'EEEE, MMM d')} — {config.ratio} ratio
        </p>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-xs font-semibold" data-testid={`${serviceLine}-staff-count`}>
            <Users className="h-3 w-3" /> {distinctStaff} {distinctStaff === 1 ? 'staff' : 'staff'} · {assigned.length} {assigned.length === 1 ? 'slot' : 'slots'}
            {unassigned.length > 0 && <span className="text-[#E65100] ml-1">/ {unassigned.length} open</span>}
          </span>
          <Button size="sm" variant="outline" onClick={onAddPattern}
            className="border-[#DED8EB] text-[#6B3FA0] hover:bg-[#EDE7F6] gap-1 h-7 text-xs" data-testid={`weekly-pattern-${serviceLine}`}>
            <CalendarDays className="h-3 w-3" /> Weekly Pattern
          </Button>
          <Button size="sm" variant="outline" onClick={onBulkAdd}
            className="border-[#DED8EB] text-[#6B3FA0] hover:bg-[#EDE7F6] gap-1 h-7 text-xs" data-testid={`bulk-add-${serviceLine}`}>
            <Users className="h-3 w-3" /> Bulk Add
          </Button>
          <Button size="sm" onClick={onAddSlot}
            className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 h-7 text-xs" data-testid={`add-participant-slot-${serviceLine}-${date}`}>
            <Plus className="h-3 w-3" /> Add Participant Shift
          </Button>
        </div>
      </div>

      {shifts.length === 0 ? (
        <div className="text-center py-8 text-[#8E85A0] border border-dashed border-[#DED8EB] rounded-lg">
          <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm font-medium">No participant shifts scheduled</p>
          <p className="text-xs mt-1">Add participant shifts first, then assign staff</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {unassigned.length > 0 && (
            <div className="mb-3">
              <p className="text-xs font-semibold text-[#E65100] uppercase tracking-wider mb-2 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Open — needs staff ({unassigned.length})
              </p>
              <div className="space-y-1.5">
                {unassigned.map(slot => (
                  <div key={slot.id}
                    className="flex items-center gap-3 p-3 rounded-lg border-2 border-dashed border-[#FFB74D] bg-[#FFF3E0]/50 transition-all hover:-translate-y-px"
                    data-testid={`open-slot-${slot.id}`}>
                    <span className="text-sm font-semibold shrink-0" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{formatTimeRange(slot.time_in, slot.time_out)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#2D1B4E]">{(slot.participants || []).join(', ') || 'No participant'}</p>
                      {slot.location && <p className="text-xs text-[#8E85A0] flex items-center gap-0.5"><MapPin className="h-3 w-3" />{slot.location}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button size="sm" onClick={() => onAssign(slot)}
                        className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-1 h-7 text-xs" data-testid={`assign-staff-${slot.id}`}>
                        <Plus className="h-3 w-3" /> Assign Staff
                      </Button>
                      <button onClick={() => onEdit(slot)} className="text-[#8E85A0] hover:text-[#6B3FA0] p-1" data-testid={`edit-slot-${slot.id}`}>
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {assigned.length > 0 && (
            <div>
              {unassigned.length > 0 && <p className="text-xs font-semibold text-[#2E7D32] uppercase tracking-wider mb-2 flex items-center gap-1">Assigned ({assigned.length})</p>}
              <div className="space-y-1.5">
                {assigned.map(shift => (
                  <div key={shift.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all hover:-translate-y-px ${SERVICE_STYLES[serviceLine]} ${shift.has_conflict ? 'ring-2 ring-[#F4A261]' : ''} ${shift.is_draft ? 'border-dashed border-[#F4A261] !bg-[#FEF6F0]' : ''}`}
                    data-testid={`slot-${shift.id}`}>
                    <span className="text-sm font-semibold shrink-0" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{formatTimeRange(shift.time_in, shift.time_out)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#2D1B4E] flex items-center gap-1.5">{(shift.participants || []).join(', ')}{shift.is_draft && <span className="text-[8px] font-bold uppercase tracking-wider text-[#E65100] bg-[#FFF3E0] px-1 py-0.5 rounded" data-testid={`draft-badge-${shift.id}`}>Draft</span>}</p>
                      <p className="text-xs opacity-75">{shift.employee_name}</p>
                    </div>
                    {shift.location && <span className="text-xs opacity-75 flex items-center gap-0.5 shrink-0"><MapPin className="h-3 w-3" />{shift.location}</span>}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => onUnassign(shift.id)} className="text-[#8E85A0] hover:text-[#D9534F] p-1" title="Unassign staff" data-testid={`unassign-${shift.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => onEdit(shift)} className="text-[#8E85A0] hover:text-[#6B3FA0] p-1" data-testid={`edit-slot-${shift.id}`}>
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
